import { useEffect, useRef } from 'react'

interface Snowflake {
  x: number
  y: number
  radius: number
  speed: number
  wind: number
  opacity: number
}

interface SnowParticlesProps {
  count?: number
  color?: string
  speed?: number
  wind?: number
  fps?: number // Add FPS control
  enabled?: boolean // Add ability to disable
  useContainer?: boolean // Use container dimensions instead of window
}

export const SnowParticles = ({
  count = 50, // Reduced default count
  color = '#ffffff',
  speed = 7,
  wind = 3,
  fps = 30, // Limit to 30fps instead of 60
  enabled = true,
  useContainer = false
}: SnowParticlesProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>()
  const snowflakesRef = useRef<Snowflake[]>([])
  const lastTimeRef = useRef<number>(0)
  const isVisibleRef = useRef<boolean>(true)

  // Pre-calculate color with opacity to avoid string operations in animation loop
  const baseColorRef = useRef<string>('')

  useEffect(() => {
    // Pre-calculate base color
    if (color.startsWith('#')) {
      baseColorRef.current = color
    } else {
      baseColorRef.current = color
    }
  }, [color])

  // Page visibility optimization
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (!enabled) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    // Performance optimization: use low DPI for better performance
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    // Initialize snowflakes with object pooling
    const initSnowflakes = () => {
      const canvasWidth = useContainer && canvas.parentElement ? canvas.parentElement.offsetWidth : window.innerWidth
      const canvasHeight = useContainer && canvas.parentElement ? canvas.parentElement.offsetHeight : window.innerHeight

      snowflakesRef.current = []
      for (let i = 0; i < count; i++) {
        snowflakesRef.current.push({
          x: Math.random() * canvasWidth,
          y: Math.random() * canvasHeight,
          radius: Math.random() * 2 + 1.5,
          speed: Math.random() * 1.5 + speed * 0.5,
          wind: Math.random() * wind - wind / 2,
          opacity: Math.random() * 0.6 + 0.3
        })
      }
    }

    // Set canvas size with performance considerations
    const resizeCanvas = () => {
      // Use container or window dimensions
      const width = useContainer && canvas.parentElement ? canvas.parentElement.offsetWidth : window.innerWidth
      const height = useContainer && canvas.parentElement ? canvas.parentElement.offsetHeight : window.innerHeight

      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
      ctx.scale(dpr, dpr)

      // Reinitialize snowflakes when canvas resizes
      initSnowflakes()
    }

    // Throttled resize handler
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(resizeCanvas, 100)
    }

    resizeCanvas()
    window.addEventListener('resize', handleResize)

    initSnowflakes()

    // FPS-limited animation loop
    const targetFrameTime = 1000 / fps

    const animate = (currentTime: number) => {
      if (!isVisibleRef.current) {
        animationRef.current = requestAnimationFrame(animate)
        return
      }

      const deltaTime = currentTime - lastTimeRef.current

      if (deltaTime >= targetFrameTime) {
        const canvasWidth = useContainer && canvas.parentElement ? canvas.parentElement.offsetWidth : window.innerWidth
        const canvasHeight =
          useContainer && canvas.parentElement ? canvas.parentElement.offsetHeight : window.innerHeight

        ctx.clearRect(0, 0, canvasWidth, canvasHeight)

        // Batch drawing operations
        ctx.globalCompositeOperation = 'source-over'

        snowflakesRef.current.forEach((snowflake) => {
          // Update position
          snowflake.y += snowflake.speed
          snowflake.x += snowflake.wind

          // Reset snowflake if it goes out of bounds (object reuse)
          if (snowflake.y > canvasHeight + 10) {
            snowflake.y = -10
            snowflake.x = Math.random() * canvasWidth
          }
          if (snowflake.x > canvasWidth + 10) {
            snowflake.x = -10
          } else if (snowflake.x < -10) {
            snowflake.x = canvasWidth + 10
          }

          // Draw snowflake with pre-calculated color
          const alpha = Math.floor(snowflake.opacity * 255)
            .toString(16)
            .padStart(2, '0')
          ctx.fillStyle = `${baseColorRef.current}${alpha}`

          ctx.beginPath()
          ctx.arc(snowflake.x, snowflake.y, snowflake.radius, 0, Math.PI * 2)
          ctx.fill()
        })

        lastTimeRef.current = currentTime
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    lastTimeRef.current = performance.now()
    animationRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [count, color, speed, wind, fps, enabled])

  if (!enabled) return null

  return (
    <canvas
      ref={canvasRef}
      className={
        useContainer ? 'pointer-events-none absolute inset-0 z-10' : 'pointer-events-none fixed inset-0 z-[100]'
      }
      style={{ mixBlendMode: 'screen' }}
    />
  )
}

export default SnowParticles
